import { Injectable, RendererFactory2, inject, isDevMode, type OnDestroy, type Renderer2 } from '@angular/core';
import { EventBusService } from '@big-direkt/event-bus';
import { DomService, PlatformService } from '@big-direkt/utils/shared';
import { BehaviorSubject } from 'rxjs';
import { type CookiebotConsent } from '../models/cookiebot-consent';
import { EnvironmentService } from './environment.service';
import { FeatureFlagsService } from './feature-flags.service';
import { WindowService } from './window.service';

@Injectable({
    providedIn: 'root',
})
export class CookiebotService implements OnDestroy {
    private readonly domService = inject(DomService);
    private readonly environmentService = inject(EnvironmentService);
    private readonly platformService = inject(PlatformService);
    private readonly renderer: Renderer2;
    private readonly rendererFactory = inject(RendererFactory2);
    private readonly windowService = inject(WindowService);
    private readonly eventBus = inject(EventBusService);
    public readonly featureFlagsService = inject(FeatureFlagsService);
    public readonly isNewCookiebot = this.featureFlagsService.isFeatureEnabled('cookiebot-without-styles');

    private boundCookieBotListener!: () => void;
    private isInitialized = false;
    public consentState$ = new BehaviorSubject<CookiebotConsent['consent']>({ marketing: false, statistics: false, preferences: false, necessary: false });

    public constructor() {
        // eslint-disable-next-line no-null/no-null
        this.renderer = this.rendererFactory.createRenderer(undefined, null);
    }

    public init(): void {
        if (this.isInitialized || this.platformService.isPlatformServer()) {
            return;
        }

        this.boundCookieBotListener = this.cookiebotOnDialogDisplay.bind(this);

        this.initCookiebot();

        this.windowService.nativeWindow()?.addEventListener('CookiebotOnDialogDisplay', this.boundCookieBotListener, { once: true });
        this.windowService.nativeWindow()?.addEventListener('CookiebotOnConsentReady', this.onConsentReadyEventListener(), { once: true });
        this.windowService.nativeWindow()?.addEventListener('CookiebotOnAccept', this.onAcceptEventListener(), { once: true });
        this.windowService.nativeWindow()?.addEventListener('CookiebotOnDecline', this.onDeclineEventListener(), { once: true });

        this.isInitialized = true;
    }

    public ngOnDestroy(): void {
        if (this.platformService.isPlatformServer()) {
            return;
        }

        this.windowService.nativeWindow()?.removeEventListener('CookiebotOnDialogDisplay', this.boundCookieBotListener);
    }

    // Add new "Einstellungen" Button
    private cookiebotOnDialogDisplay(): void {
        if(this.isNewCookiebot()) {
            this.windowService.document().getElementById('CybotCookiebotDialog')?.classList.add('feature-flagged');

            return;
        }

        const cookiebotIdPrefix = 'CybotCookiebotDialogBodyLevel';

        const btnSettings: HTMLAnchorElement = this.renderer.createElement('a') as HTMLAnchorElement;
        btnSettings.text = 'Einstellungen';
        btnSettings.classList.add('CybotCookiebotDialogBodyButton');
        btnSettings.id = `${cookiebotIdPrefix}ButtonLevelOptinAllowallSettings`;

        btnSettings.addEventListener(
            'click',
            (): void => {
                this.windowService.document().getElementById(`${cookiebotIdPrefix}Buttons`)?.classList.toggle('hidden');
            },
            false,
        );

        // Insert Settings Button
        this.windowService.document().getElementById(`${cookiebotIdPrefix}ButtonLevelOptinAllowallSelectionWrapper`)?.prepend(btnSettings);

        // Move the "Speichern" Button to Details-Section
        this.windowService
            .document()
            .getElementById(`${cookiebotIdPrefix}DetailsWrapper`)
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            ?.prepend(this.windowService.document().getElementById(`${cookiebotIdPrefix}ButtonLevelOptinAllowallSelection`)!);

        // Hide the Details-Section
        this.windowService.document().getElementById(`${cookiebotIdPrefix}Buttons`)?.classList.add('hidden');
    }

    private onAcceptEventListener(): () => void {
        return (): void => {
            const cookiebot: CookiebotConsent | undefined = this.windowService.nativeWindow()?.Cookiebot;

            if (!cookiebot) {
                return;
            }

            const { statistics, marketing }: { statistics: boolean; marketing: boolean } = cookiebot.consent;

            if (!statistics || !marketing) {
                this.eventBus.emit({ key: 'datalayer_reset_event' });

                return;
            }

            if (this.environmentService.gtmId) {
                this.initGtm();
            }

            if (!isDevMode()) {
                this.initAblyft();
            } else {
                console.info('did not initialize ablyft tool in dev environment'); // eslint-disable-line no-console
            }
        };
    }

    private onConsentReadyEventListener(): () => void {
        return (): void => {
            const consent = this.windowService.nativeWindow()?.Cookiebot.consent;

            if (!consent) {
                return;
            }

            const { marketing, statistics, preferences, necessary } = consent;
            this.consentState$.next({ marketing, statistics, preferences, necessary });
        };
    }

    private onDeclineEventListener(): () => void {
        return (): void => {
            this.eventBus.emit({ key: 'datalayer_reset_event' });
        };
    }

    private initGtm(): void {
        this.eventBus.emit({ key: 'datalayer_initialize_event' });

        if (!this.environmentService.gtmUrl || !this.environmentService.gtmId) {
            return
        }

        const gtmUrl = `${this.environmentService.gtmUrl}?id=${this.environmentService.gtmId}`;
        this.domService.createScriptTag(gtmUrl, {
            target: this.windowService.document().head,
            async: true,
            type: 'text/plain',
            data: {
                cookieconsent: 'statistics',
            },
        });
    }

    private initAblyft(): void {
        const url = 'https://ablyft.big-direkt.de/js-data';

        this.domService.createScriptTag(url, {
            target: this.windowService.document().head,
            async: true,
            type: 'text/plain',
            data: {
                cookieconsent: 'statistics',
            },
        });
    }

    private initCookiebot(): void {
        const cookieBotUrl = 'https://consent.cookiebot.com/uc.js';

        this.domService.createScriptTag(cookieBotUrl, {
            target: this.windowService.document().head,
            async: true,
            id: 'Cookiebot',
            data: {
                cbid: this.environmentService.cookieBotId,
            },
        });
    }
}
